// Angular
import { BehaviorSubject, map, takeUntil } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';

// Libraries
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

// Environment
import { environment } from '../../../../environments/environment';

// Models
import { Job, JobData, JobStatus } from '../models';
import { RecordChange } from '../models/record-change';
import { AppCleanup } from '../../../shared/models';

// Services
import { JobHub } from './job.hub';
import { JobService } from './job.service';

// Enums
import { TargetType } from '../enums/target-type.enum';
import { RecordChangeSourceType } from '../../../shared/enums/record-change-source-type.enum';

@Injectable({
	providedIn: 'root',
})
export class DeviceHub extends AppCleanup implements OnDestroy {
	protected dataReceivedVar: BehaviorSubject<Array<JobData>> = new BehaviorSubject<Array<JobData>>(null);
	dataReceived$ = this.dataReceivedVar.asObservable();
	protected recordChangeVar: BehaviorSubject<RecordChange> = new BehaviorSubject<RecordChange>(null);
	recordChange$ = this.recordChangeVar.asObservable();
	protected refreshFromDisconnectVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	refreshFromDisconnect$ = this.refreshFromDisconnectVar.asObservable();
	// dataReceived = new EventEmitter<Array<JobData>>();
	protected jobStartedVar: BehaviorSubject<Job> = new BehaviorSubject<Job>(null);
	jobStarted$ = this.jobStartedVar.asObservable();

	protected connectionEstablishedVar: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
	connectionEstablished$ = this.connectionEstablishedVar.asObservable();
	connected: boolean = false;

	onReconnected: (connectionId?: string) => void;
	onInitialConnect: () => void;
	onReconnecting: (error?: Error) => void;
	onInitialConnecting: () => void;
	onClose: (error?: Error) => void;
	private _initialReconnectFired: boolean = false;

	private _hubConnection: HubConnection;
	reconnectStart: Date = null;
	private _machineName: string;

	constructor(
		private jobService: JobService,
		private jobHub: JobHub
	) {
		super();
	}

	initHub(machineName: string) {
		this._machineName = machineName;
		this.createConnection();
		this.registerOnServerEvents();
		this.startConnection();

		this.connectionEstablished$.pipe(takeUntil(this.destroy)).subscribe((connectionEstablished) => {
			if (connectionEstablished) {
				// this had to be binded to get _keys
				this.onReconnecting = this._onReconnecting.bind(this);
				this.onReconnected = this._onReconnected.bind(this);
				this.onInitialConnecting = this._onReconnecting.bind(this);
				this.onInitialConnect = this._onReconnected.bind(this);
				this.registerHubEvents();
				this.subscribeToJobData();
			}
		});
	}

	private createConnection() {
		this._hubConnection = new HubConnectionBuilder().configureLogging(LogLevel.None).withUrl(this.deviceHubUrl).withAutomaticReconnect().build();
		this._hubConnection.keepAliveIntervalInMilliseconds = 5000;
		this._hubConnection.serverTimeoutInMilliseconds = 15000;
	}

	private registerHubEvents() {
		if (this.onReconnected) this._hubConnection.onreconnected(this.onReconnected);

		if (this.onReconnecting) this._hubConnection.onreconnecting(this.onReconnecting);

		if (this.onClose) this._hubConnection.onclose(this.onClose);
	}

	startSendingData() {
		if (this._machineName) {
			this._hubConnection
				.invoke('StartSendingData', [this._machineName])
				.then(() => {
					this.connected = true;
				})
				.catch((err) => console.error(err));
		} else {
			console.error('Machine name not set');
		}
	}

	startConnection(): Promise<void> {
		return this._hubConnection
			.start()
			.then(() => {
				this.connectionEstablishedVar.next(true);

				if (this.onInitialConnect && this._initialReconnectFired) this.onInitialConnect();

				this.startSendingData();
			})
			.catch(() => {
				setTimeout(() => {
					if (this.onInitialConnecting && !this._initialReconnectFired) {
						this.onInitialConnecting();
						this._initialReconnectFired = true;
					}

					this.startConnection();
				}, 5000);
			});
	}

	disconnect(keys: string[]) {
		console.log('Disconnected');
		this._hubConnection
			.invoke('StopSendingData', keys)
			.then(() => {
				if (this._hubConnection.state === HubConnectionState.Connected) {
					this._hubConnection.stop();
					this.connected = false;
				}
			})
			.catch((err) => console.error(err));
	}

	registerOnServerEvents() {
		this.registerHubEvents();

		//TJD = TransferJobData. Using TJD to reduce message size
		this._hubConnection.on(TargetType.TJD, (data: Array<JobData>) => {
			if (data) {
				data.map((d) => {
					d.d;
				});
			}
			this.dataReceivedVar.next(data);
		});

		this._hubConnection.on(TargetType.JOB_STARTED, (data: Job) => {
			if (data && (data.status === JobStatus.InProgress || data.status === JobStatus.Completed)) {
				this.jobStartedVar.next(data);
			}
		});

		this._hubConnection.on(TargetType.RECORD_CHANGE, (data: RecordChange) => {
			data.source = RecordChangeSourceType.PORTAL;
			this.jobHub.emitRecordChange(data);
		});
	}

	subscribeToDevice() {
		if (window.navigator.onLine) this.startSendingData();
	}

	private _onReconnecting() {
		console.log('Reconnecting');
	}

	private _onReconnected() {
		console.log('Reconnected');
		this.startSendingData();
	}

	subscribeToJobData() {
		this.dataReceived$
			.pipe(
				map((data: Array<JobData>) => {
					if (data && data.length > 0) {
						data.forEach((d) => {
							d.d = new Date(d.d);
						});
					}
					return data;
				}),
				takeUntil(this.destroy)
			)
			.subscribe((data: Array<JobData>) => {
				if (data && data.length > 0) {
					this.jobService.addPointToListener(data);
				}
			});
	}

	ngOnDestroy() {
		this.disconnect([this._machineName]);
	}
}
